/**
 * `/groups/:id` — one group and who is in it.
 *
 * Membership is idempotent on the server: adding somebody who is already a
 * member succeeds and changes nothing, so two administrators doing the same
 * thing at once do not collide. Nothing here checks first.
 *
 * Renaming and deleting are declared once in
 * [`../entities/groupActions.tsx`](../entities/groupActions.tsx) and rendered
 * in the header. A delete cascades to memberships, roles and grants, and its
 * confirmation counts them first; afterwards this screen has nothing to show,
 * so it goes back to the list.
 */
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { SubjectDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { FormDialog } from '../../components/FormDialog';
import { EntityDetail } from '../../components/EntityDetail';
import { SubjectPicker } from '../../components/SubjectPicker';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubjectSearch } from '../../hooks/useSubjectSearch';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { useSelfLockout } from '../../auth/useSelfLockout';
import { useGroupActions } from '../entities/groupActions';
import {
  addGroupMember,
  getGroup,
  listGroupMembers,
  removeGroupMember,
  searchSubjects,
} from '../../api/identity';

export default function GroupDetailPage() {
  const { t } = useTranslation('people');
  const { id } = useParams();
  const groupId = Number(id);
  const { can } = useAuth();
  const navigate = useNavigate();

  const group = useAsync(() => getGroup(groupId), [groupId]);
  const list = useListQuery({ defaultSort: 'displayName', resetKey: groupId });
  const members = useAsync(() => listGroupMembers(groupId, list.query), [groupId, list.key]);

  const [adding, setAdding] = useState(false);
  const [subject, setSubject] = useState<SubjectDto | null>(null);
  const candidates = useSubjectSearch((query) => searchSubjects(query, { type: 'user' }), {
    enabled: adding,
  });

  // Closing forgets the pick and the term, so the next open starts empty.
  function closeAdding(): void {
    setAdding(false);
    setSubject(null);
    candidates.reset();
  }

  const mayManage = can('group.manage_members');

  // Rename and delete, from `../entities/groupActions`. `EntityDetail` renders
  // the buttons and mounts the dialogs.
  const actions = useGroupActions({
    onChanged: (saved) => group.setData({ group: saved }),
    onDeleted: () => navigate('/groups'),
  });

  const add = useSubmit(async () => {
    if (!subject) return undefined;
    const result = await addGroupMember(groupId, { userId: subject.id });
    setSubject(null);
    candidates.reset();
    members.reload();
    return result;
  });

  // Removing yourself can take the group's roles away from you; the server
  // says so, and `lockout` asks before it repeats the call.
  const lockout = useSelfLockout();
  const remove = useSubmit(async (userId: number) => {
    const result = await lockout.guard((accept) => removeGroupMember(groupId, userId, accept));
    members.reload();
    return result;
  });

  return (
    <AsyncContent state={group}>
      {({ group: record }) => (
        <EntityDetail
          backTo="/groups"
          backLabel={t('groupDetail.back')}
          title={record.name}
          description={record.description ?? t('groups.noDescription')}
          entity={actions.forRow(record)}
          actions={
            mayManage && (
              <Button
                variant="contained"
                startIcon={<PersonAddAltIcon />}
                onClick={() => setAdding(true)}
              >
                {t('groupDetail.addMember')}
              </Button>
            )
          }
          error={remove.error}
        >
          <AsyncContent state={members}>
            {({ members: rows, page }) => (
              <EntityTable
                rows={rows}
                rowKey={(member) => member.id}
                list={list}
                page={page}
                actionsWidth={56}
                empty={{
                  dense: true,
                  title: t('groupDetail.emptyTitle'),
                  description: t('groupDetail.emptyDescription'),
                }}
                columns={[
                  {
                    field: 'displayName',
                    label: t('groupDetail.column.name'),
                    sortable: true,
                    render: (member) => (
                      <Link
                        component={RouterLink}
                        to={`/users/${member.id}`}
                        underline="hover"
                        color="inherit"
                      >
                        {member.displayName}
                      </Link>
                    ),
                  },
                  {
                    field: 'email',
                    label: t('groupDetail.column.email'),
                    sortable: true,
                    render: (member) => (
                      <Typography variant="body2" color="text.secondary">
                        {member.email}
                      </Typography>
                    ),
                  },
                ]}
                actions={(member) => [
                  {
                    key: 'remove',
                    label: t('groupDetail.removeMember'),
                    icon: <PersonRemoveIcon fontSize="small" />,
                    tone: 'error',
                    onClick: () => void remove.submit(member.id),
                    disabled: remove.pending,
                    hidden: !mayManage,
                  },
                ]}
              />
            )}
          </AsyncContent>

          <FormDialog
            open={adding}
            title={t('groupDetail.addDialog.title', { name: record.name })}
            submitLabel={t('groupDetail.addDialog.submit')}
            pending={add.pending}
            error={add.error}
            disabled={subject === null}
            onClose={closeAdding}
            onSubmit={async () => {
              if (await add.submit()) closeAdding();
            }}
          >
            <SubjectPicker
              options={candidates.options}
              value={subject}
              loading={candidates.loading}
              label={t('groupDetail.addDialog.user')}
              onChange={setSubject}
              onQueryChange={candidates.setQuery}
              helperText={t('groupDetail.addDialog.help')}
              autoFocus
            />
          </FormDialog>
          {lockout.dialog}
        </EntityDetail>
      )}
    </AsyncContent>
  );
}
